import { Component, EventEmitter, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HcToastrService } from '@manager-app/service/toastr.service';
import { StorageInvoicesConfirmComponent } from '@manager-app/storage/storage-invoices-confirm/storage-invoices-confirm.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DataService } from '@manager-app/service/data.service';
import { addMinutes } from 'date-fns';
import { DomSanitizer } from '@angular/platform-browser';
import {
  AppEventModel,
  HxCurrencyPipe,
  HxEventService,
  HxFioPipe,
  HxStorageInvoiceService,
  HxStoreService,
  HxUiLabelPipe,
  StorageInvoiceModel,
  uiLabel
} from 'hx-services';
import { Subscription } from 'rxjs';
import { StorageInvoicePayModal } from '@manager-app/storage/storage-invoices-list/storage-invoice-pay-modal/storage-invoice-pay.modal';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ErrorHandlerService } from '@manager-app/service/error-handler.service';
import { DatePipe, JsonPipe, NgClass, NgFor, NgIf } from '@angular/common';

@Component({
    selector: 'app-storage-invoice-view.m-grid__item.m-grid__item--fluid.m-wrapper',
    templateUrl: './storage-invoice-view.component.html',
    styleUrls: ['./storage-invoice-view.component.css'],
    imports: [NgIf, RouterLink, NgClass, NgFor, JsonPipe, DatePipe, TranslocoPipe, HxCurrencyPipe, HxFioPipe, HxUiLabelPipe]
})
export class StorageInvoiceViewComponent implements OnInit, OnDestroy {
  @ViewChild('templateRef') public templateRef: TemplateRef<any>;
  id: number;
  isLoading = {
    invoice: false,
    submit: false,
    paid: false,
  };
  invoice?: StorageInvoiceModel;
  events: AppEventModel[] = [];
  statusList = DataService.statusList;
  eventList = DataService.eventList;
  image = {
    name: null,
    path: null,
    mimeType: null
  };
  baseLink = `/api/vn/files/download/`;
  @Output() updateData = new EventEmitter<any>();
  private sub: Subscription;

  constructor(
    private aRoute: ActivatedRoute,
    private storageInvoiceService: HxStorageInvoiceService,
    private eventService: HxEventService,
    private router: Router,
    private toastr: HcToastrService,
    private modal: NgbModal,
    private domSanitizer: DomSanitizer,
    private storeService: HxStoreService,
    private tr: TranslocoService,
    private errorService: ErrorHandlerService,
  ) {
  }

  ngOnInit() {
    this.sub = this.aRoute.params.subscribe(params => {
      this.id = params['id'] || null;
      this.loadData();
      this.loadEvents();
    });
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  showModal(storageInvoiceId: number, action: string): void {
    const modal = this.modal.open(StorageInvoicesConfirmComponent, {size: 'lg'});
    modal.componentInstance.storageInvoiceId = storageInvoiceId;
    modal.componentInstance.action = action;
  }

  hasPermissionCancel(status: string, sentDate: string, receivedDate: string): boolean {
    const d = addMinutes(new Date(receivedDate), 20);
    return status === 'ACCEPTED' && !sentDate && d > new Date();
  }

  hasPermissionReject(status: string, sentDate: string, receivedDate: string): boolean {
    const d = addMinutes(new Date(receivedDate), 20);
    return status === 'ACCEPTED' && sentDate && d > new Date();
  }

  showImage(file): void {
    this.image.name = file.fileName;
    this.image.path = this.baseLink + file.guid;
    this.image.mimeType = file.mimeType;
    if (file.mimeType === 'application/pdf') {
      this.image.path = this.domSanitizer.bypassSecurityTrustResourceUrl(this.image.path);
    }

    this.modal.open(this.templateRef, {size: 'lg'}).result
      .then(() => this.resetImage())
      .catch(() => this.resetImage());
  }

  closeModal(modal: any) {
    modal.dismiss();
    this.updateData.emit(true);
  }

  goBack(): void {
    if (window.history.length > 0) {
      window.history.back();
    } else {
      this.router.navigate(['../'], {relativeTo: this.aRoute});
    }
  }

  paidConfirm() {
    this.storeService.getAvailableForPaymentApprovals().subscribe(result => {
      const availableStoreIds = result.map(r => r.id);
      let notPermittedStore = undefined;
      if (availableStoreIds.includes(this.invoice.store.id)) {
        notPermittedStore = uiLabel(this.tr.getActiveLang(), this.invoice.store.title);
      }
      if (!notPermittedStore) {
        const modalRef = this.modal.open(StorageInvoicePayModal);
        modalRef.componentInstance.invoiceIds = [this.invoice.id];
        modalRef.componentInstance.storeIds = [this.invoice.store.id];
        modalRef.result.then(() => {
          this.router.navigate(['../'], {relativeTo: this.aRoute});
        }, () => {
          modalRef.dismiss();
        });
      } else {
        this.toastr.error('storage.invoice.paid.restriction', {storeTitles: notPermittedStore});
      }
    }, err => {
      this.errorService.handler(err.error);
    });
  }

  private loadEvents() {
    this.eventService.getStorageInvoiceEventList(this.id)
      .then(result => this.events = result, err => console.error(err));
  }

  private loadData() {
    if (this.id) {
      this.invoice = undefined;
      this.isLoading.invoice = true;

      this.storageInvoiceService.getStorageInvoiceById(this.id).subscribe(result => {
        this.invoice = result;
        this.isLoading.invoice = false;
      }, () => this.isLoading.invoice = false);
    }
  }

  private resetImage() {
    this.image.name = null;
    this.image.path = '';
  }
}
